import { Card, Checkbox, Flex } from "antd"
import { EllipsisIcon } from "icons"
import { Text } from "ui/Typography"
import styles from "./clients.module.css"
import { memo, useCallback, useMemo, useState } from "react"
import { formatPhoneNumber } from "utils/data-formatters"
import GroupTags from "pagesV2/pro/clients/GroupTags"
import { spacings } from "themes/variables"
import PropTypes from "prop-types"
import ActionDrawers from "./Drawers/ActionDrawer"
import BulkActions from "./ClientTable/BulkActions"
import { useNavigate } from "react-router-dom"
import { routes } from "router"
import SyncGroupDrawer from "./Drawers/SyncGroupDrawer"
import { useTranslation } from "react-i18next"
import { CheckClientDelete, cleanClient } from "store/clients"
import { useDispatch, useSelector } from "react-redux"
import { usePermissions } from "hooks/usePermissions"
import { MODULES, ACTIONS } from "utils/permissions"

function ListingCard({
  clients,
  selectedGroups,
  setSelectedGroups,
  groupAction,
  setGroupAction,
  selectedClientIds,
  setSelectedClientIds,
  removeGroups,
  setRemoveGroups,
  isMenuListHide,
  setIsMenuListHide,
  handleFetchClients,
  setSelectedClient,
  handleSelectedGroupsChange,
  setHasDeleteClient,
  setClientId
}) {
  const [open, setOpen] = useState(false)
  const [selectedRowKeys, setSelectedRowKeys] = useState([])
  const navigate = useNavigate()
  const [isGroupDrawerVisible, setIsGroupDrawerVisible] = useState(false)
  const [selectedClientId, setSelectedClientId] = useState(null)
  const [selectedClientRecord, setSelectedClientRecord] = useState(null)
  const [userId, setUserId] = useState([])
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { metadata } = useSelector((store) => store.clients)
  const { hasPermission } = usePermissions()
  const canEditClient = hasPermission(MODULES.CLIENTS, ACTIONS.EDIT)
  const canViewMessages = hasPermission(MODULES.MESSAGES, ACTIONS.VIEW)

  const handleTableActions = useCallback(
    (client) => {
      const { client_groups, id, user_id } = client || {}
      setOpen(true)
      setSelectedGroups(client_groups)
      setSelectedClientId(id)
      setSelectedClientIds([id])
      setUserId(user_id)
      setSelectedClientRecord(client)
    },
    [setSelectedGroups, setSelectedClientIds]
  )

  const handleSelectChange = (id, checked) => {
    if (checked) {
      setSelectedRowKeys((prev) => [...prev, id])
    } else {
      setSelectedRowKeys((prev) => prev.filter((key) => key !== id))
    }
  }
  const handleMenuClick = (type, client) => {
    const templates = metadata[`${type}_templates_collection`]
    const userIds = [client?.user_id]
    if (type === "email") {
      navigate(routes.guideClientEmailActionPath(), { state: { clients: [client], userIds, templates } })
    } else if (type === "text") {
      navigate(routes.guideClientTextActionPath(), { state: { clients: [client], userIds, templates } })
    }
  }

  const handleActionClick = async (action) => {
    const templates = metadata[`${action}_templates_collection`]
    const userIds = [userId]
    if (action === "view") {
      navigate(routes.guideClientPath({ id: selectedClientId }))
    }
    if (action === "edit") {
      dispatch(cleanClient())
      navigate(routes.guideClientEditPath({ id: selectedClientId }))
    } else if (action === "add_to_group") {
      setIsGroupDrawerVisible(true)
      setIsMenuListHide(false)
      setGroupAction("add")
    } else if (action === "remove_from_group") {
      setIsGroupDrawerVisible(true)
      setIsMenuListHide(true)
      setGroupAction("remove")
    } else if (action === "message") {
      navigate(routes.guideClientMessageActionPath(), { state: { clients, userIds, templates } })
    } else if (action === "text") {
      // Guard the handler too — the action is disabled without a phone, but re-enabling
      // it via dev tools must not open the SMS flow for an unreachable client.
      if (!selectedClientRecord?.user?.phone_number) return
      navigate(routes.guideClientTextActionPath(), { state: { clients, userIds, templates } })
    } else if (action === "delete") {
      setHasDeleteClient(true)
      setClientId(selectedClientId)
      await dispatch(CheckClientDelete(selectedClientId))
      setOpen(false)
    }
  }

  const drawerActions = useMemo(() => {
    const groups = selectedClientRecord?.client_groups || []
    const actions = [
      {
        label: "View",
        color: "primary",
        variant: "outlined",
        onClick: () => handleActionClick("view")
      }
    ]

    if (canEditClient) {
      actions.push({
        label: "Edit",
        color: "primary",
        variant: "outlined",
        onClick: () => handleActionClick("edit")
      })
    }

    actions.push({
      label: "Add to Group",
      color: "primary",
      variant: "outlined",
      onClick: () => handleActionClick("add_to_group"),
      disabled: !canEditClient
    })

    actions.push({
      label: "Remove from Group",
      color: "primary",
      variant: "outlined",
      onClick: () => handleActionClick("remove_from_group"),
      disabled: !canEditClient || groups.length === 0
    })

    if (canViewMessages) {
      actions.push({
        label: "Message",
        color: "primary",
        variant: "outlined",
        onClick: () => handleActionClick("message")
      })
    }

    actions.push({
      label: "Text",
      color: "primary",
      variant: "outlined",
      onClick: () => handleActionClick("text"),
      disabled: !selectedClientRecord?.user?.phone_number
    })

    if (canEditClient) {
      actions.push({
        label: "Delete",
        color: "danger",
        variant: "outlined",
        onClick: () => handleActionClick("delete")
      })
    }

    return actions
  }, [selectedClientRecord, canEditClient, canViewMessages])

  const handleBulkGroupAction = (action, selectedKeys) => {
    const selectedClients = clients.filter((client) => selectedKeys.includes(client.user_id))

    const clientIds = selectedClients.map((client) => client.id)
    const clientGroups = selectedClients.flatMap((client) => client.client_groups || [])

    const uniqueGroupsMap = new Map()
    ;[...selectedGroups, ...clientGroups].forEach((group) => {
      if (group?.group_id && !uniqueGroupsMap.has(group.group_id)) {
        uniqueGroupsMap.set(group.group_id, group)
      }
    })

    setSelectedClientIds(clientIds)
    setSelectedGroups(Array.from(uniqueGroupsMap.values()))
    setGroupAction(action)
    setIsGroupDrawerVisible(true)
    setIsMenuListHide(action === "bulk_remove")
    setSelectedClient(null)
    setRemoveGroups([])
  }

  const hasSelected = selectedRowKeys.length > 0

  return (
    <>
      {hasSelected && (
        <BulkActions
          clients={clients}
          selectedRowKeys={selectedRowKeys}
          setSelectedRowKeys={setSelectedRowKeys}
          onGroupAction={handleBulkGroupAction}
          canEditClient={canEditClient}
        />
      )}
      {clients?.map((client) => {
        const { user, user_id } = client || {}
        const { phone_number, first_name, last_name, email } = user || {}
        return (
          <Card key={user_id} className={`w-full bg-surface-1 ${styles.listingCard}`}>
            <Flex gap={spacings.L} className="w-full" vertical>
              <Flex gap={spacings.M} align="center">
                <Checkbox
                  className={styles.hideOnMobile}
                  checked={selectedRowKeys.includes(user_id)}
                  onChange={(e) => handleSelectChange(user_id, e.target.checked)}
                />
                <Flex vertical gap={spacings.XS}>
                  <Flex>
                    <Text className={styles.label}>Name</Text> {first_name} {last_name}
                  </Flex>
                  <Flex>
                    <Text className={styles.label}>Email</Text>
                    <button type="button" className={`${styles.contactLink} break-all`} onClick={(e) => handleMenuClick("email", client)}>
                      {email}
                    </button>
                  </Flex>
                  <Flex>
                    <Text className={styles.label}>Phone</Text>
                    <button type="button" className={`${styles.contactLink} break-all`} onClick={(e) => handleMenuClick("text", client)}>
                      {formatPhoneNumber(phone_number)}
                    </button>
                    {/* <a href={`tel:${phone_number}`} onClick={(e) => e.stopPropagation()}>
                      {formatPhoneNumber(phone_number)}
                    </a> */}
                  </Flex>
                  <Flex>
                    <Text className={styles.label}>Group</Text>{" "}
                    <GroupTags
                      client={client}
                      setIsGroupModalVisible={setIsGroupDrawerVisible}
                      setSelectedGroups={setSelectedGroups}
                      setGroupAction={setGroupAction}
                      setSelectedClientIds={setSelectedClientIds}
                      setRemoveGroups={setRemoveGroups}
                      setIsMenuListHide={setIsMenuListHide}
                      canEditClient={canEditClient}
                    />
                  </Flex>
                </Flex>
                <button
                  type="button"
                  onClick={() => handleTableActions(client)}
                  className="cursor-pointer ml-auto"
                  aria-label="More actions"
                  style={{ background: "none", border: "none", padding: 0 }}
                >
                  <EllipsisIcon fill="#525455" width={20} height={20} />
                </button>
              </Flex>
            </Flex>
          </Card>
        )
      })}
      {open && (
        <ActionDrawers actionType="actions" open={open} setOpen={setOpen} handleActionClick={handleActionClick} actions={drawerActions} />
      )}
      {isGroupDrawerVisible && (
        <SyncGroupDrawer
          title={groupAction === "add" ? t("clients.add_selected_clients_to_group") : t("clients.remove_selected_clients_from_group")}
          open={isGroupDrawerVisible}
          setOpen={setIsGroupDrawerVisible}
          selectedGroups={selectedGroups}
          isMenuListHide={isMenuListHide}
          removeGroups={removeGroups}
          selectedClientIds={selectedClientIds}
          groupAction={groupAction}
          handleFetchClients={handleFetchClients}
          setSelectedClient={setSelectedClient}
          setGroupAction={setGroupAction}
          setIsMenuListHide={setIsMenuListHide}
          setSelectedGroups={setSelectedGroups}
          setIsGroupModalVisible={setIsGroupDrawerVisible}
          setSelectedClientIds={setSelectedClientIds}
          handleSelectedGroupsChange={handleSelectedGroupsChange}
        />
      )}
    </>
  )
}

ListingCard.propTypes = {
  clients: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      user: PropTypes.shape({
        phone_number: PropTypes.string,
        first_name: PropTypes.string,
        last_name: PropTypes.string,
        email: PropTypes.string
      }),
      client_groups: PropTypes.array
    })
  ),
  selectedGroups: PropTypes.array,
  setSelectedGroups: PropTypes.func,
  groupAction: PropTypes.string,
  setGroupAction: PropTypes.func,
  selectedClientIds: PropTypes.array,
  setSelectedClientIds: PropTypes.func,
  removeGroups: PropTypes.arrayOf(PropTypes.object),
  setRemoveGroups: PropTypes.func,
  isMenuListHide: PropTypes.bool,
  setIsMenuListHide: PropTypes.func,
  handleFetchClients: PropTypes.func,
  setSelectedClient: PropTypes.func,
  handleSelectedGroupsChange: PropTypes.func,
  setHasDeleteClient: PropTypes.func,
  setClientId: PropTypes.func
}

export default memo(ListingCard)
